import React from 'react';
import {
  Archive,
  CheckCircle2,
  Clock3,
  Inbox,
  Pencil,
  Plus,
} from 'lucide-react';
import * as api from '../services/vcNoticeApi';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import CustomersNoticeEditor from './CustomersNoticeEditor';
import CustomersNoticeTip from './CustomersNoticeTip';
import './CustomersManualClienteView.css';
import './CustomersNoticesView.css';

const TABS = [
  { id: 'todos', label: 'Todos', Icon: Inbox },
  { id: 'atuais', label: 'Em andamento', Icon: CheckCircle2 },
  { id: 'pendentes', label: 'Agendados', Icon: Clock3 },
  { id: 'rascunhos', label: 'Rascunhos', Icon: Pencil },
  { id: 'encerrados', label: 'Encerrados', Icon: Archive },
];

function statusLabel(status) {
  if (status === 'published') return 'Publicado';
  if (status === 'scheduled') return 'Agendado';
  if (status === 'archived') return 'Encerrado';
  return 'Rascunho';
}

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default class CustomersNoticesView extends React.Component {
  state = {
    tab: 'todos',
    items: [],
    counts: { todos: 0, atuais: 0, pendentes: 0, rascunhos: 0, encerrados: 0 },
    loading: true,
    error: null,
    editorId: null,
    notice: null,
    editorLoading: false,
  };

  componentDidMount() {
    this.reloadList();
  }

  reloadList = async () => {
    if (this.abort) this.abort.abort();
    const ac = new AbortController();
    this.abort = ac;
    this.setState({ loading: true, error: null });
    try {
      const data = await api.listNotices(this.state.tab, ac.signal);
      this.setState({
        items: data.items || [],
        counts: data.counts || this.state.counts,
        loading: false,
      });
    } catch (e) {
      if (e.name === 'AbortError') return;
      this.setState({ loading: false, error: e.message || 'Falha ao carregar avisos.' });
    }
  };

  setTab = (tab) => {
    this.setState({ tab }, () => this.reloadList());
  };

  openEditor = async (id) => {
    this.setState({ editorId: id, editorLoading: true, notice: null, error: null });
    try {
      const notice = await api.getNotice(id);
      this.setState({ notice, editorLoading: false });
    } catch (e) {
      this.setState({ editorLoading: false, error: e.message || 'Falha ao abrir o aviso.' });
    }
  };

  createNotice = async () => {
    this.setState({ error: null });
    try {
      const created = await api.createNotice('Novo aviso');
      await this.openEditor(created.id);
    } catch (e) {
      this.setState({ error: e.message || 'Falha ao criar aviso.' });
    }
  };

  closeEditor = async () => {
    this.setState({ editorId: null, notice: null, editorLoading: false });
    await this.reloadList();
  };

  reloadEditor = async () => {
    if (!this.state.editorId) return;
    const notice = await api.getNotice(this.state.editorId);
    this.setState({ notice });
  };

  render() {
    const s = this.state;
    const inEditor = s.editorId != null;

    return (
      <div className="cmc-manual cmc-notice">
        <aside className="cmc-manual-sidebar cmc-notice-sidebar">
          <div className="cmc-manual-brand cmc-notice-brand">
            <div className="cmc-notice-brand-text">
              <div className="cmc-manual-brand-title">Comunicação</div>
              <div className="cmc-manual-brand-sub">Publicações e avisos</div>
            </div>
            <CustomersNoticeTip label="Novo aviso" placement="bottom">
              <button
                type="button"
                className="cmc-notice-new-btn"
                onClick={this.createNotice}
                aria-label="Novo aviso"
              >
                <Plus size={15} strokeWidth={2.25} aria-hidden />
              </button>
            </CustomersNoticeTip>
          </div>
          <nav className="cmc-notice-nav">
            {TABS.map((t) => {
              const Icon = t.Icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={'cmc-notice-nav-item' + (s.tab === t.id && !inEditor ? ' is-active' : '')}
                  onClick={() => {
                    if (inEditor) this.setState({ editorId: null, notice: null });
                    this.setTab(t.id);
                  }}
                >
                  <span className="cmc-notice-nav-left">
                    <Icon size={15} strokeWidth={1.85} className="cmc-notice-nav-icon" aria-hidden />
                    <span>{t.label}</span>
                  </span>
                  <span className="cmc-notice-nav-count">{s.counts[t.id] || 0}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="cmc-manual-main">
          {inEditor ? (
            s.editorLoading || !s.notice ? (
              <div className="cmc-manual-scroll">
                {s.error ? <div className="cmc-notice-error">{s.error}</div> : null}
                <ProcessConfigSkeleton variant="listBody" ariaLabel="Carregando aviso" />
              </div>
            ) : (
              <CustomersNoticeEditor
                key={s.notice.id}
                notice={s.notice}
                hasOtherInProgress={
                  (s.counts.atuais || 0) > 0 && s.notice.status !== 'published'
                }
                onBack={this.closeEditor}
                onReload={this.reloadEditor}
                onDeleted={this.closeEditor}
              />
            )
          ) : (
            <div className="cmc-manual-scroll">
              {s.error ? <div className="cmc-notice-error">{s.error}</div> : null}
              <div className="cmc-notice-list-wrap">
                <div className="cmc-manual-ov-head">
                  <div>
                    <h1 className="cmc-manual-ov-title">Publicações e avisos</h1>
                    <p className="cmc-manual-ov-sub cmc-manual-grad-text">
                      Publique agora ou programe a exibição. Gerencie rascunhos, em andamento e agendados.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="cmc-manual-primary-btn cmc-manual-primary-btn--inline"
                    onClick={this.createNotice}
                  >
                    + Novo aviso
                  </button>
                </div>

                {s.loading ? (
                  <ProcessConfigSkeleton variant="listBody" ariaLabel="Carregando avisos" />
                ) : s.items.length === 0 ? (
                  <div className="cmc-manual-topic-empty">
                    <div className="cmc-manual-topic-empty-title">Nenhum aviso nesta lista</div>
                    <div className="cmc-manual-topic-empty-sub">
                      Crie um aviso com texto formatado, imagens e vídeo do YouTube.
                    </div>
                    <button
                      type="button"
                      className="cmc-manual-accent-btn cmc-manual-accent-btn--lg"
                      onClick={this.createNotice}
                    >
                      + Criar aviso
                    </button>
                  </div>
                ) : (
                  <div className="cmc-notice-cards">
                    {s.items.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="cmc-notice-card"
                        onClick={() => this.openEditor(item.id)}
                      >
                        <div className="cmc-notice-card-top">
                          <span className={'cmc-notice-chip cmc-notice-chip--' + item.status}>
                            {item.status === 'draft' ? (
                              <Pencil size={11} strokeWidth={2} aria-hidden className="cmc-notice-chip-icon" />
                            ) : null}
                            {statusLabel(item.status)}
                          </span>
                          <span className="cmc-notice-card-when">
                            {item.status === 'scheduled'
                              ? `Exibe ${formatWhen(item.publishAt)}`
                              : item.status === 'published'
                                ? `Desde ${formatWhen(item.publishedAt || item.publishAt)}`
                                : formatWhen(item.updatedAt)}
                          </span>
                        </div>
                        <div className="cmc-notice-card-title">{item.title || 'Sem título'}</div>
                        <div className="cmc-notice-card-excerpt">{item.excerpt || 'Sem conteúdo ainda'}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }
}
